import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Button, Table, Form, Modal, Badge, Spinner, Alert, Pagination } from 'react-bootstrap';
import apiService from '../services/api';

const EnterpriseTeams = () => {
  const [teams, setTeams] = useState([]);
  const [memberCounts, setMemberCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // All/Active/Inactive
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [saving, setSaving] = useState(false);

  // Members modal state
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [membersTeam, setMembersTeam] = useState(null);
  const [members, setMembers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [membersSaving, setMembersSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true
  });

  useEffect(() => {
    loadTeams();
  }, [search, statusFilter]);

  const loadTeams = async () => {
    setLoading(true);
    setError('');

    try {
      const [teamsResponse, assignmentsResponse] = await Promise.all([
        apiService.getEnterpriseTeams(search, statusFilter === '' ? undefined : statusFilter === 'active'),
        apiService.getEnterpriseTeamUsers()
      ]);

      if (teamsResponse.success) {
        setTeams(teamsResponse.data || []);
      } else {
        setError(teamsResponse.error || 'Failed to load enterprise teams');
      }

      if (assignmentsResponse.success) {
        const counts = (assignmentsResponse.data || []).reduce((acc, assignment) => {
          const teamId = assignment.enterprise_team_id;
          acc[teamId] = (acc[teamId] || 0) + 1;
          return acc;
        }, {});
        setMemberCounts(counts);
      }
    } catch (error) {
      console.error('Error loading enterprise teams:', error);
      setError('Failed to load enterprise teams');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e.key === 'Enter' || e.type === 'blur') {
      setCurrentPage(1);
      setSearch(e.target.value);
    }
  };

  const handleStatusFilterChange = (e) => {
    setCurrentPage(1);
    setStatusFilter(e.target.value);
  };

  const handleShowModal = (team = null) => {
    if (team) {
      setEditingTeam(team);
      setFormData({
        name: team.name || '',
        description: team.description || '',
        is_active: team.is_active !== undefined ? !!team.is_active : true
      });
    } else {
      setEditingTeam(null);
      setFormData({
        name: '',
        description: '',
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingTeam(null);
    setFormData({
      name: '',
      description: '',
      is_active: true
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      setError('Team name is required');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const teamData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        is_active: !!formData.is_active
      };

      let response;
      if (editingTeam) {
        response = await apiService.updateEnterpriseTeam(editingTeam.id, teamData);
      } else {
        response = await apiService.createEnterpriseTeam(teamData);
      }

      if (response.success) {
        handleCloseModal();
        await loadTeams();
      } else {
        setError(response.error || 'Failed to save enterprise team');
      }
    } catch (error) {
      console.error('Error saving enterprise team:', error);
      setError('Failed to save enterprise team');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (team) => {
    const action = team.is_active ? 'archive' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} the team "${team.name}"?`)) {
      return;
    }

    try {
      const response = team.is_active
        ? await apiService.archiveEnterpriseTeam(team.id)
        : await apiService.activateEnterpriseTeam(team.id);

      if (response.success) {
        await loadTeams();
      } else {
        setError(response.error || `Failed to ${action} enterprise team`);
      }
    } catch (error) {
      console.error(`Error trying to ${action} enterprise team:`, error);
      setError(`Failed to ${action} enterprise team`);
    }
  };

  const handleDelete = async (team) => {
    if (!window.confirm(`Are you sure you want to delete the team "${team.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await apiService.deleteEnterpriseTeam(team.id);
      if (response.success) {
        await loadTeams();
      } else {
        setError(response.error || 'Failed to delete enterprise team');
      }
    } catch (error) {
      console.error('Error deleting enterprise team:', error);
      setError('Failed to delete enterprise team');
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const handleManageMembers = async (team) => {
    setMembersTeam(team);
    setMembers([]);
    setAvailableUsers([]);
    setSelectedUserIds([]);
    setShowMembersModal(true);
    await loadMembers(team);
  };

  const loadMembers = async (team) => {
    setMembersLoading(true);

    try {
      const [membersResponse, usersResponse] = await Promise.all([
        apiService.getEnterpriseTeamMembers(team.id),
        apiService.getEnterpriseUsers('enterprise', 1, 1000, '')
      ]);

      const teamMembers = membersResponse.success ? (membersResponse.data || []) : [];
      setMembers(teamMembers);
      if (!membersResponse.success) {
        setError(membersResponse.error || 'Failed to load team members');
      }

      if (usersResponse.success) {
        const memberIds = teamMembers.map(member => member.user_account_id);
        const unassigned = (usersResponse.data || [])
          .filter(user => !memberIds.includes(user.user_account_id))
          .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
        setAvailableUsers(unassigned);
      } else {
        setError(usersResponse.error || 'Failed to load enterprise users');
      }
    } catch (error) {
      console.error('Error loading team members:', error);
      setError('Failed to load team members');
    } finally {
      setMembersLoading(false);
    }
  };

  const handleCloseMembersModal = () => {
    setShowMembersModal(false);
    setMembersTeam(null);
    setMembers([]);
    setAvailableUsers([]);
    setSelectedUserIds([]);
  };

  const toggleSelectedUser = (userAccountId) => {
    setSelectedUserIds(prev => (
      prev.includes(userAccountId)
        ? prev.filter(id => id !== userAccountId)
        : [...prev, userAccountId]
    ));
  };

  const handleAddMembers = async () => {
    if (!membersTeam || selectedUserIds.length === 0) return;

    setMembersSaving(true);
    setError('');

    try {
      const response = await apiService.assignUsersToEnterpriseTeam(membersTeam.id, selectedUserIds);
      if (response.success) {
        setSelectedUserIds([]);
        await loadMembers(membersTeam);
        await loadTeams();
      } else {
        setError(response.error || 'Failed to add team members');
      }
    } catch (error) {
      console.error('Error adding team members:', error);
      setError('Failed to add team members');
    } finally {
      setMembersSaving(false);
    }
  };

  const handleRemoveMember = async (member) => {
    if (!membersTeam) return;
    if (!window.confirm(`Are you sure you want to remove "${member.user_name || member.user_account_id}" from "${membersTeam.name}"?`)) {
      return;
    }

    setMembersSaving(true);
    setError('');

    try {
      const response = await apiService.removeUserFromEnterpriseTeam(membersTeam.id, member.user_account_id);
      if (response.success) {
        await loadMembers(membersTeam);
        await loadTeams();
      } else {
        setError(response.error || 'Failed to remove team member');
      }
    } catch (error) {
      console.error('Error removing team member:', error);
      setError('Failed to remove team member');
    } finally {
      setMembersSaving(false);
    }
  };

  // The enterprise team endpoint returns the full list, so paginate on the client
  const totalPages = Math.max(1, Math.ceil(teams.length / itemsPerPage));
  const pageStartIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTeams = teams.slice(pageStartIndex, pageStartIndex + itemsPerPage);

  return (
    <div>
      {/* Header */}
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="bi bi-people me-2"></i>
              Enterprise Teams
            </h2>
            <Button variant="primary" onClick={() => handleShowModal()}>
              <i className="bi bi-plus-circle me-2"></i>
              Add Team
            </Button>
          </div>
        </Col>
      </Row>

      {/* Error Alert */}
      {error && (
        <Row className="mb-4">
          <Col>
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              <i className="bi bi-exclamation-triangle me-2"></i>
              {error}
            </Alert>
          </Col>
        </Row>
      )}

      {/* Search and Filter */}
      <Row className="mb-4">
        <Col md={6}>
          <Form.Control
            type="text"
            placeholder="Search teams by name..."
            onKeyDown={handleSearch}
            onBlur={handleSearch}
          />
        </Col>
        <Col md={6} className="text-end">
          <Form.Select
            style={{ width: 'auto', display: 'inline-block' }}
            className="me-2"
            value={statusFilter}
            onChange={handleStatusFilterChange}
          >
            <option value="">All statuses</option>
            <option value="active">Active Only</option>
            <option value="inactive">Not Active Only</option>
          </Form.Select>
          <Form.Select
            style={{ width: 'auto', display: 'inline-block' }}
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
          >
            <option value="10">10 per page</option>
            <option value="25">25 per page</option>
            <option value="50">50 per page</option>
          </Form.Select>
        </Col>
      </Row>

      {/* Teams Table */}
      <Row>
        <Col>
          <Card>
            <Card.Body className="p-0">
              {loading ? (
                <div className="text-center p-4">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                </div>
              ) : teams.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <i className="bi bi-people display-1"></i>
                  <div className="mt-2">No enterprise teams found</div>
                  <div className="small">Create your first team to get started</div>
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th>Team Name</th>
                      <th>Description</th>
                      <th>Members</th>
                      <th>Status</th>
                      <th>Created Date</th>
                      <th>Last Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTeams.map((team) => (
                      <tr key={team.id}>
                        <td>
                          <div className="fw-medium">{team.name}</div>
                        </td>
                        <td>{team.description || '-'}</td>
                        <td>
                          <Badge bg="info">{memberCounts[team.id] || 0}</Badge>
                        </td>
                        <td>
                          <Badge bg={team.is_active ? 'success' : 'secondary'}>
                            {team.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td>{team.created_at ? apiService.formatDate(team.created_at) : '-'}</td>
                        <td>{team.last_updated_at ? apiService.formatDate(team.last_updated_at) : '-'}</td>
                        <td>
                          <div className="d-flex gap-1">
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => handleShowModal(team)}
                              title="Edit"
                            >
                              <i className="bi bi-pencil"></i>
                            </Button>
                            <Button
                              variant="outline-primary"
                              size="sm"
                              onClick={() => handleManageMembers(team)}
                              title="Manage Members"
                            >
                              <i className="bi bi-person-lines-fill"></i>
                            </Button>
                            <Button
                              variant={team.is_active ? 'outline-warning' : 'outline-success'}
                              size="sm"
                              onClick={() => handleToggleStatus(team)}
                              title={team.is_active ? 'Archive' : 'Activate'}
                            >
                              <i className={`bi ${team.is_active ? 'bi-archive' : 'bi-check-circle'}`}></i>
                            </Button>
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={() => handleDelete(team)}
                              title="Delete"
                            >
                              <i className="bi bi-trash"></i>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>

            {/* Pagination */}
            {totalPages > 1 && (
              <Card.Footer>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="text-muted">
                    Showing {pageStartIndex + 1} to {Math.min(pageStartIndex + itemsPerPage, teams.length)} of {teams.length} entries
                  </div>
                  <Pagination className="mb-0">
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                    {[...Array(totalPages)].map((_, i) => (
                      <Pagination.Item
                        key={i + 1}
                        active={i + 1 === currentPage}
                        onClick={() => handlePageChange(i + 1)}
                      >
                        {i + 1}
                      </Pagination.Item>
                    ))}
                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              </Card.Footer>
            )}
          </Card>
        </Col>
      </Row>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} size="md">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingTeam ? 'Edit Enterprise Team' : 'Add Enterprise Team'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSave}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Team Name *</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter team name (e.g., Customer Experience, Finance)"
                required
                maxLength={100}
              />
              <Form.Text className="text-muted">
                Maximum 100 characters. Must be unique.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Describe what this team is responsible for"
                maxLength={500}
              />
              <Form.Text className="text-muted">
                Optional. Maximum 500 characters.
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="team-is-active-switch"
                name="is_active"
                label="Active"
                checked={!!formData.is_active}
                onChange={handleInputChange}
              />
              <Form.Text className="text-muted">
                Inactive teams are archived and cannot be assigned to users.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                  Saving...
                </>
              ) : (
                'Save Team'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Manage Members Modal */}
      <Modal show={showMembersModal} onHide={handleCloseMembersModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {membersTeam ? `Manage Members - ${membersTeam.name}` : 'Manage Members'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {membersLoading ? (
            <div className="text-center p-3">
              <Spinner animation="border" />
            </div>
          ) : (
            <>
              <h6 className="mb-3">
                <i className="bi bi-people me-2"></i>
                Current Members ({members.length})
              </h6>
              {members.length === 0 ? (
                <div className="text-center py-3 text-muted border rounded mb-4">
                  <i className="bi bi-person-x display-6"></i>
                  <div className="mt-2">This team has no members yet</div>
                </div>
              ) : (
                <Table responsive hover size="sm" className="mb-4">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => (
                      <tr key={member.id || member.user_account_id}>
                        <td>{member.user_name || '-'}</td>
                        <td>{member.user_email || '-'}</td>
                        <td>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveMember(member)}
                            disabled={membersSaving}
                            title="Remove from team"
                          >
                            <i className="bi bi-person-dash"></i>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              <h6 className="mb-3">
                <i className="bi bi-person-plus me-2"></i>
                Add Members
              </h6>
              {availableUsers.length === 0 ? (
                <div className="text-center py-3 text-muted border rounded">
                  <div>All enterprise users are already members of this team</div>
                </div>
              ) : (
                <div
                  className="border rounded p-2"
                  style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.25rem 1rem', maxHeight: '30vh', overflowY: 'auto' }}
                >
                  {availableUsers.map((user) => (
                    <Form.Check
                      type="checkbox"
                      key={user.user_account_id}
                      id={`team-user-${user.user_account_id}`}
                      label={user.email ? `${user.name} (${user.email})` : user.name}
                      checked={selectedUserIds.includes(user.user_account_id)}
                      onChange={() => toggleSelectedUser(user.user_account_id)}
                      disabled={membersSaving}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseMembersModal} disabled={membersSaving}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleAddMembers}
            disabled={membersSaving || membersLoading || selectedUserIds.length === 0}
          >
            {membersSaving ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                Saving...
              </>
            ) : (
              `Add Selected (${selectedUserIds.length})`
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EnterpriseTeams;
